import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Building2,
  CreditCard,
  Users,
  TrendingUp,
  Plus,
  Settings,
  Trash2,
  RefreshCw,
  DollarSign,
  Package,
  Edit,
  Eye,
  Smartphone,
  Bot,
  AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function SaaSAdmin() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Form states
  const [agencyForm, setAgencyForm] = useState({
    name: "",
    email: "",
    company_name: "",
    plan_id: "",
  });

  const [planForm, setPlanForm] = useState({
    name: "",
    key: "",
    description: "",
    price_monthly: 0,
    price_yearly: 0,
    limits: {
      max_sessions: 1,
      max_businesses: 1,
      max_agents_per_account: 3,
      max_workflows_per_account: 5,
    },
    features: {
      ai_agents: true,
      workflows: true,
      whatsapp_gateway: true,
      voice_notes: false,
      broadcasts: false,
      white_label: false,
    },
    is_active: true,
  });

  useEffect(() => {
    if (user?.role === "admin") {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, agenciesRes, plansRes] = await Promise.all([
        api.get("/saas/dashboard/stats"),
        api.get("/saas/agencies"),
        api.get("/saas/plans?include_inactive=true"),
      ]);
      setStats(statsRes.data);
      setAgencies(agenciesRes.data.agencies || []);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      toast.error("Error loading data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencyDetails = async (agencyId) => {
    setLoadingDetails(true);
    try {
      const [agencyRes, usageRes, subsRes] = await Promise.all([
        api.get(`/saas/agencies/${agencyId}`),
        api.get(`/saas/agencies/${agencyId}/usage`),
        api.get(`/saas/agencies/${agencyId}/sub-accounts`),
      ]);
      setAgencyDetails({
        ...agencyRes.data,
        usage: usageRes.data.usage,
        limits: usageRes.data.limits,
        sub_accounts: subsRes.data.sub_accounts || [],
      });
    } catch (error) {
      toast.error("Error loading agency details");
      console.error(error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const openAgencyDetails = (agency) => {
    setSelectedAgency(agency);
    fetchAgencyDetails(agency.id);
  };

  const handleCreateAgency = async () => {
    try {
      await api.post("/saas/agencies", agencyForm);
      toast.success("Agency created successfully");
      setShowAgencyModal(false);
      setAgencyForm({ name: "", email: "", company_name: "", plan_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating agency");
    }
  };

  const handleUpdateAgency = async () => {
    try {
      await api.put(`/saas/agencies/${editingAgency.id}`, agencyForm);
      toast.success("Agency updated successfully");
      setEditingAgency(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating agency");
    }
  };

  const handleDeleteAgency = async (agencyId) => {
    if (!window.confirm("Are you sure you want to delete this agency?")) return;
    try {
      await api.delete(`/saas/agencies/${agencyId}?force=true`);
      toast.success("Agency deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting agency");
    }
  };

  const handleCreatePlan = async () => {
    try {
      await api.post("/saas/plans", planForm);
      toast.success("Plan created successfully");
      setShowPlanModal(false);
      resetPlanForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating plan");
    }
  };

  const handleUpdatePlan = async () => {
    try {
      await api.put(`/saas/plans/${editingPlan.id}`, planForm);
      toast.success("Plan updated successfully");
      setEditingPlan(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating plan");
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await api.delete(`/saas/plans/${planId}`);
      toast.success("Plan deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting plan");
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: "",
      key: "",
      description: "",
      price_monthly: 0,
      price_yearly: 0,
      limits: {
        max_sessions: 1,
        max_businesses: 1,
        max_agents_per_account: 3,
        max_workflows_per_account: 5,
      },
      features: {
        ai_agents: true,
        workflows: true,
        whatsapp_gateway: true,
        voice_notes: false,
        broadcasts: false,
        white_label: false,
      },
      is_active: true,
    });
  };

  const openEditAgency = (agency) => {
    setEditingAgency(agency);
    setAgencyForm({
      name: agency.name,
      email: agency.email,
      company_name: agency.company_name || "",
      plan_id: agency.plan_id || "",
    });
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      key: plan.key || "",
      description: plan.description || "",
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly || 0,
      limits: plan.limits || {},
      features: plan.features || {},
      is_active: plan.is_active,
    });
  };

  if (user?.role !== "admin") {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-red-500">{t("saas.accessDenied")}</h1>
        <p className="text-muted-foreground">{t("saas.superAdminRequired")}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="saas-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("saas.title")}</h1>
          <p className="text-muted-foreground">{t("saas.subtitle")}</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {t("saas.refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("saas.totalAgencies")}</p>
                  <p className="text-2xl font-bold">{stats.total_agencies}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("saas.activeAgencies")}</p>
                  <p className="text-2xl font-bold">{stats.active_agencies}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("saas.mrr")}</p>
                  <p className="text-2xl font-bold">${stats.mrr?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">ARR</p>
                  <p className="text-2xl font-bold">${stats.arr?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {t("saas.subscriptionPlans")}
          </CardTitle>
          <Button onClick={() => setShowPlanModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t("saas.newPlan")}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <Card key={plan.id} className={`border-2 ${!plan.is_active ? "opacity-50" : ""}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditPlan(plan)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => handleDeletePlan(plan.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-2xl font-bold">
                    ${plan.price_monthly}<span className="text-sm font-normal">/mo</span>
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {plan.key && (
                    <div className="flex justify-between">
                      <span>Clave billing:</span>
                      <Badge variant="outline" className="font-mono">{plan.key}</Badge>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>{t('saas.waNumbersTotal')}:</span>
                    <span className="font-medium">{plan.limits?.max_sessions ?? "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subcuentas (total):</span>
                    <span className="font-medium">{plan.limits?.max_businesses ?? "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Agents:</span>
                    <span className="font-medium">{plan.limits?.max_agents_per_account}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {plan.features?.white_label && <Badge variant="secondary">White Label</Badge>}
                    {plan.features?.broadcasts && <Badge variant="secondary">Broadcasts</Badge>}
                    {plan.features?.voice_notes && <Badge variant="secondary">Voice Notes</Badge>}
                  </div>
                  {!plan.is_active && <Badge variant="destructive">Inactive</Badge>}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Agencies Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t("saas.agencies")}
          </CardTitle>
          <Button onClick={() => setShowAgencyModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t("saas.newAgency")}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {agencies.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No agencies yet</p>
            ) : (
              agencies.map((agency) => (
                <div
                  key={agency.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{agency.name}</p>
                      <p className="text-sm text-muted-foreground">{agency.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={agency.status === "active" ? "default" : "destructive"}>
                      {agency.status}
                    </Badge>
                    <Badge variant="outline">{agency.plan_name || "No Plan"}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {agency.sub_accounts_count || 0} sub-accounts
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openAgencyDetails(agency)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditAgency(agency)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => handleDeleteAgency(agency.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Agency Modal */}
      <Dialog open={showAgencyModal || !!editingAgency} onOpenChange={(open) => {
        if (!open) {
          setShowAgencyModal(false);
          setEditingAgency(null);
          setAgencyForm({ name: "", email: "", company_name: "", plan_id: "" });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAgency ? t("saas.editAgency") : t("saas.createAgency")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Agency Name</Label>
              <Input
                value={agencyForm.name}
                onChange={(e) => setAgencyForm({ ...agencyForm, name: e.target.value })}
                placeholder="My Agency"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={agencyForm.email}
                onChange={(e) => setAgencyForm({ ...agencyForm, email: e.target.value })}
                placeholder="agency@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={agencyForm.company_name}
                onChange={(e) => setAgencyForm({ ...agencyForm, company_name: e.target.value })}
                placeholder="Company LLC"
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select
                value={agencyForm.plan_id}
                onValueChange={(value) => setAgencyForm({ ...agencyForm, plan_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.filter(p => p.is_active).map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.price_monthly}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              onClick={editingAgency ? handleUpdateAgency : handleCreateAgency}
            >
              {editingAgency ? "Update Agency" : "Create Agency"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Plan Modal */}
      <Dialog open={showPlanModal || !!editingPlan} onOpenChange={(open) => {
        if (!open) {
          setShowPlanModal(false);
          setEditingPlan(null);
          resetPlanForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? t("saas.editPlan") : t("saas.createPlan")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plan Name</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  placeholder="Pro Plan"
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Price ($)</Label>
                <Input
                  type="number"
                  value={planForm.price_monthly}
                  onChange={(e) => setPlanForm({ ...planForm, price_monthly: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Clave del plan (billing key)</Label>
              <Input
                value={planForm.key || ""}
                onChange={(e) => setPlanForm({ ...planForm, key: e.target.value.trim().toLowerCase() })}
                placeholder="solo / agencia"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Liga este plan con la compra real: cuando el pago de un cliente detecta esta palabra
                en el nombre del producto (ej. "agenc" → agencia), se le aplican los límites TOTALES de aquí.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                placeholder="Plan description..."
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-1">{t('saas.realLimits')}</h4>
              <p className="text-xs text-muted-foreground mb-3">
                Estos dos campos controlan lo que el cliente realmente puede usar: cuántos números de
                WhatsApp y cuántas subcuentas en TOTAL (los extras comprados se suman encima).
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('saas.waNumbersTotal')}</Label>
                  <Input
                    type="number"
                    value={planForm.limits.max_sessions ?? 1}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      limits: { ...planForm.limits, max_sessions: parseInt(e.target.value) || 1 }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subcuentas (total)</Label>
                  <Input
                    type="number"
                    value={planForm.limits.max_businesses ?? 1}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      limits: { ...planForm.limits, max_businesses: parseInt(e.target.value) || 1 }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Agents per Account</Label>
                  <Input
                    type="number"
                    value={planForm.limits.max_agents_per_account}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      limits: { ...planForm.limits, max_agents_per_account: parseInt(e.target.value) || 1 }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Workflows per Account</Label>
                  <Input
                    type="number"
                    value={planForm.limits.max_workflows_per_account}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      limits: { ...planForm.limits, max_workflows_per_account: parseInt(e.target.value) || 1 }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('saas.aiCreditsPerMonth')}</Label>
                  <Input
                    type="number"
                    value={planForm.limits.ai_credits_monthly ?? 0}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      limits: { ...planForm.limits, ai_credits_monthly: parseInt(e.target.value) || 0 }
                    })}
                  />
                  <p className="text-xs text-muted-foreground">Respuestas con IA de plataforma incluidas al mes. El cliente puede comprar paquetes extra o usar su propia llave.</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Features</h4>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(planForm.features).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <Label className="capitalize">{key.replace(/_/g, " ")}</Label>
                    <Switch
                      checked={value}
                      onCheckedChange={(checked) => setPlanForm({
                        ...planForm,
                        features: { ...planForm.features, [key]: checked }
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <Label>Plan Active</Label>
              <Switch
                checked={planForm.is_active}
                onCheckedChange={(checked) => setPlanForm({ ...planForm, is_active: checked })}
              />
            </div>

            <Button
              className="w-full"
              onClick={editingPlan ? handleUpdatePlan : handleCreatePlan}
            >
              {editingPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agency Details Modal */}
      <Dialog open={!!selectedAgency} onOpenChange={(open) => {
        if (!open) {
          setSelectedAgency(null);
          setAgencyDetails(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedAgency?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : agencyDetails ? (
            <div className="space-y-6 py-4">
              {/* Usage Overview */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Usage vs Limits
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Sub-accounts</span>
                      <span className={
                        agencyDetails.limits?.max_sub_accounts !== -1 && 
                        agencyDetails.usage?.sub_accounts > agencyDetails.limits?.max_sub_accounts
                          ? "text-red-500 font-medium"
                          : ""
                      }>
                        {agencyDetails.usage?.sub_accounts || 0} / {agencyDetails.limits?.max_sub_accounts === -1 ? "∞" : agencyDetails.limits?.max_sub_accounts}
                      </span>
                    </div>
                    <Progress 
                      value={agencyDetails.limits?.max_sub_accounts === -1 ? 0 : Math.min(
                        ((agencyDetails.usage?.sub_accounts || 0) / (agencyDetails.limits?.max_sub_accounts || 1)) * 100,
                        100
                      )}
                      className={
                        agencyDetails.limits?.max_sub_accounts !== -1 &&
                        agencyDetails.usage?.sub_accounts > agencyDetails.limits?.max_sub_accounts
                          ? "[&>div]:bg-red-500"
                          : agencyDetails.limits?.max_sub_accounts === -1 ? "[&>div]:bg-green-500" : ""
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>WhatsApp Sessions</span>
                      <span>{agencyDetails.usage?.total_whatsapp_sessions || 0} {agencyDetails.limits?.max_whatsapp_per_account === -1 ? "/ ∞" : ""}</span>
                    </div>
                    <Progress 
                      value={agencyDetails.limits?.max_whatsapp_per_account === -1 ? 0 : Math.min(
                        ((agencyDetails.usage?.total_whatsapp_sessions || 0) / 
                        ((agencyDetails.limits?.max_whatsapp_per_account || 5) * (agencyDetails.usage?.sub_accounts || 1))) * 100,
                        100
                      )}
                      className={agencyDetails.limits?.max_whatsapp_per_account === -1 ? "[&>div]:bg-green-500" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>AI Agents</span>
                      <span>
                        {agencyDetails.usage?.total_agents || 0} / {agencyDetails.limits?.max_agents_per_account === -1 ? "∞" : agencyDetails.limits?.max_agents_per_account}
                      </span>
                    </div>
                    <Progress 
                      value={agencyDetails.limits?.max_agents_per_account === -1 ? 0 : Math.min(
                        ((agencyDetails.usage?.total_agents || 0) / (agencyDetails.limits?.max_agents_per_account || 1)) * 100,
                        100
                      )}
                      className={agencyDetails.limits?.max_agents_per_account === -1 ? "[&>div]:bg-green-500" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Workflows</span>
                      <span>
                        {agencyDetails.usage?.total_workflows || 0} / {agencyDetails.limits?.max_workflows_per_account === -1 ? "∞" : agencyDetails.limits?.max_workflows_per_account}
                      </span>
                    </div>
                    <Progress 
                      value={agencyDetails.limits?.max_workflows_per_account === -1 ? 0 : Math.min(
                        ((agencyDetails.usage?.total_workflows || 0) / (agencyDetails.limits?.max_workflows_per_account || 1)) * 100,
                        100
                      )}
                      className={agencyDetails.limits?.max_workflows_per_account === -1 ? "[&>div]:bg-green-500" : ""}
                    />
                  </div>
                </div>
                
                {agencyDetails.limits?.max_sub_accounts !== -1 && 
                 agencyDetails.usage?.sub_accounts > agencyDetails.limits?.max_sub_accounts && (
                  <div className="mt-3 flex items-center gap-2 text-red-500 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    Agency exceeds plan limits. Consider upgrading.
                  </div>
                )}
              </div>

              {/* Sub-accounts List */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Sub-accounts ({agencyDetails.sub_accounts?.length || 0})
                </h4>
                <div className="space-y-2">
                  {agencyDetails.sub_accounts?.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No sub-accounts</p>
                  ) : (
                    agencyDetails.sub_accounts?.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{sub.name}</p>
                          <p className="text-xs text-muted-foreground">{sub.id}</p>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <div className="flex items-center gap-1">
                            <Smartphone className="w-4 h-4 text-green-500" />
                            <span>{sub.whatsapp_count || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Bot className="w-4 h-4 text-blue-500" />
                            <span>{sub.agents_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* White Label Status */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  White Label
                </h4>
                <div className="p-3 border rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Status</span>
                    <Badge variant={agencyDetails.white_label?.enabled ? "default" : "secondary"}>
                      {agencyDetails.white_label?.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  {agencyDetails.white_label?.enabled && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>App Name</span>
                        <span>{agencyDetails.white_label?.app_name || "Default"}</span>
                      </div>
                      {agencyDetails.white_label?.custom_domain && (
                        <div className="flex justify-between text-sm">
                          <span>Custom Domain</span>
                          <span>{agencyDetails.white_label?.custom_domain}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
